import type { ApiResponse, Product } from '~/types'
import { readShopStorage, writeShopStorage } from '~/utils/shopStorage'

const STORAGE_SUFFIX = 'recently_viewed'
const LEGACY_GLOBAL_KEY = 'cdt-recently-viewed'
const MAX_ITEMS = 12

interface StoredProductRef {
  id: number
  slug: string
  viewedAt: number
}

function readLegacyGlobalRecentlyViewed(): string | null {
  if (!import.meta.client) return null
  return localStorage.getItem(LEGACY_GLOBAL_KEY)
}

function readStored(slug: string): StoredProductRef[] {
  if (!import.meta.client || !slug) return []

  try {
    let raw = readShopStorage(slug, STORAGE_SUFFIX)
    if (!raw) {
      const legacy = readLegacyGlobalRecentlyViewed()
      if (legacy) {
        writeShopStorage(slug, STORAGE_SUFFIX, legacy)
        localStorage.removeItem(LEGACY_GLOBAL_KEY)
        raw = legacy
      }
    }

    if (!raw) return []
    const parsed = JSON.parse(raw) as StoredProductRef[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeStored(slug: string, items: StoredProductRef[]) {
  if (!import.meta.client || !slug) return
  writeShopStorage(slug, STORAGE_SUFFIX, JSON.stringify(items.slice(0, MAX_ITEMS)))
}

export function useRecentlyViewed() {
  const shopSlug = useShopSlug()
  const stored = ref<StoredProductRef[]>([])

  function hydrate() {
    if (!import.meta.client) return
    stored.value = readStored(shopSlug.value)
  }

  function recordView(product: Pick<Product, 'id' | 'slug'>) {
    if (!import.meta.client || !product.id || !shopSlug.value) return

    const next = [
      { id: product.id, slug: product.slug, viewedAt: Date.now() },
      ...readStored(shopSlug.value).filter((item) => item.id !== product.id),
    ].slice(0, MAX_ITEMS)

    writeStored(shopSlug.value, next)
    stored.value = next
  }

  async function fetchProducts(excludeProductId?: number): Promise<Product[]> {
    hydrate()
    const ids = stored.value
      .filter((item) => item.id !== excludeProductId)
      .map((item) => item.id)

    if (!ids.length) return []

    try {
      const response = await $fetch<ApiResponse<Product[]>>(
        `${useShopApiBase().value}/products/compare?ids=${ids.join(',')}`,
      )
      if (!response.success || !response.data?.length) return []

      const byId = new Map(response.data.map((product) => [product.id, product]))
      return ids
        .map((id) => byId.get(id))
        .filter((product): product is Product => Boolean(product))
    } catch {
      return []
    }
  }

  watch(shopSlug, () => {
    hydrate()
  }, { immediate: true })

  return {
    stored,
    hydrate,
    recordView,
    fetchProducts,
  }
}
